import { useState, useRef, useEffect } from 'react';
import './Profile.css';
import { api, usingApi } from './api';
import { scopedKey } from './scope';
import { passwordError } from './password';
import PasswordInput from './PasswordInput';
import RichEditor from './RichEditor';
import { htmlHasContent, sanitizeHtml } from './sanitizeHtml';
import { formatPhone } from './phone';
import Icon from './Icon';
import { SOCIALS, SOCIAL_KEYS, socialHandle, socialLink } from './social';
import { PROFILE_SECTION_KEYS } from './AgentLanding';

// Labels for the public profile's movable sections (keys defined by AgentLanding).
const SECTION_LABELS = {
  stats: 'Sales stats', about: 'About', tools: 'Home value & affordability buttons',
  reviews: 'Client reviews', areas: 'Areas served', properties: 'Properties (map & list)',
  reports: 'Market Reports', charts: 'Charts & Statistics', calc: 'Mortgage calculator',
};
// Sections whose public HEADING the agent can rename (e.g. to Chinese). stats/tools
// render no heading text, so there's nothing to rename there. Default headings shown
// as placeholders; blank input = use the default.
const SECTION_DEFAULT_TITLES = {
  about: 'About {name}', reviews: 'Client reviews', areas: 'Areas served',
  properties: 'Properties', reports: 'Market Reports', charts: 'Charts & Statistics',
  calc: 'Estimate your monthly payment',
};
// Normalize a stored order: keep known keys, then append any missing in default order.
function normalizeSectionOrder(stored) {
  const out = Array.isArray(stored) ? stored.filter(k => PROFILE_SECTION_KEYS.includes(k)) : [];
  for (const k of PROFILE_SECTION_KEYS) if (!out.includes(k)) out.push(k);
  return out;
}

function fmtTime(iso) { return iso ? new Date(iso).toLocaleString() : ''; }

// Downscale an uploaded image to a small square-ish thumbnail data URL (keeps localStorage small).
function resizeImage(file, maxSize, cb) {
  const reader = new FileReader();
  reader.onload = e => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      cb(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
}

// Spoken-language options (match the app's supported locales).
const LANG_OPTIONS = [['en', 'English'], ['zh', 'Chinese'], ['es', 'Spanish']];
// Social handles shown on the public profile (keys/labels come from the shared social module).
// Mirror of the server slugify, for the live URL preview.
function clientSlugify(s) {
  return String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

function Profile({ agent, onSave, onLogout, mlsEditable = false, orgMlsName = null, onGettingStarted }) {
  const [photo, setPhoto]       = useState(agent.photo || '');
  const [alias, setAlias]       = useState(agent.alias || '');
  const [email, setEmail]       = useState(agent.email || '');
  const [phone, setPhone]       = useState(agent.phone || '');
  const [website, setWebsite]   = useState(agent.website || '');
  const [mls, setMls]           = useState(agent.mls || '');
  const [licenseId, setLicenseId] = useState(agent.licenseId || '');
  const [mlsBrokerId, setMlsBrokerId] = useState(agent.mlsBrokerId || '');
  const [languages, setLanguages] = useState(Array.isArray(agent.languages) ? agent.languages : []);
  const toggleLang = (code) => setLanguages(ls => ls.includes(code) ? ls.filter(c => c !== code) : [...ls, code]);
  const [username, setUsername] = useState(agent.username || '');
  const [mlsOptions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(scopedKey('ira_mls', agent.id)) || '[]'); } catch { return []; }
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [signature, setSignature] = useState(agent.signature || '');
  const [publicProfile, setPublicProfile] = useState(!!agent.publicProfile);
  const [publicSlug, setPublicSlug] = useState(agent.publicSlug || '');
  // The last SAVED publish state (what's actually live). Separate from the edit
  // controls above so toggling without saving doesn't show "Live", and so the live
  // link appears immediately after Save without needing a page refresh.
  const [savedPub, setSavedPub] = useState({ on: !!agent.publicProfile, slug: agent.publicSlug || '' });
  useEffect(() => { setSavedPub({ on: !!agent.publicProfile, slug: agent.publicSlug || '' }); }, [agent.publicProfile, agent.publicSlug]);
  const [bio, setBio]           = useState(agent.bio || '');
  const [socials, setSocials]   = useState(agent.socials && typeof agent.socials === 'object' ? agent.socials : {});
  const [sectionOrder, setSectionOrder] = useState(() => normalizeSectionOrder(agent.sectionOrder));
  // Sections UNCHECKED (hidden) on the public profile; empty = show everything.
  const [hiddenSections, setHiddenSections] = useState(() => new Set(
    Array.isArray(agent.hiddenSections) ? agent.hiddenSections.filter(k => PROFILE_SECTION_KEYS.includes(k)) : [],
  ));
  // Custom public headings per section (e.g. Chinese); missing/blank = default.
  const [sectionTitles, setSectionTitles] = useState(() => (
    (agent.sectionTitles && typeof agent.sectionTitles === 'object') ? { ...agent.sectionTitles } : {}
  ));
  const [msg, setMsg]           = useState('');
  const [credMsg, setCredMsg]   = useState('');
  const [sigMsg, setSigMsg]     = useState('');
  const [pubMsg, setPubMsg]     = useState('');
  const fileRef = useRef(null);
  const sigRef = useRef(null);

  // The public profile is a personal *selling* page (sold properties + direct contact),
  // so it's for agents only — not org admins or the system operator. (Brokerage-level
  // public presence will be a separate /org/<slug> page — see memory.)
  const canPublicProfile = !agent.admin && !agent.system;
  const previewSlug = clientSlugify(publicSlug || alias || username) || 'your-name';
  const profileUrl = `${window.location.origin}/agent/${previewSlug}`;

  // Live availability check for the chosen handle (debounced). Advisory only — the server
  // still guarantees a unique slug on save (clean name → name-<key> fallback).
  const [slugState, setSlugState] = useState({ status: 'idle' });   // idle | checking | available | taken
  useEffect(() => {
    if (!usingApi() || !publicProfile) { setSlugState({ status: 'idle' }); return; }
    const s = clientSlugify(publicSlug || alias || username);
    if (!s) { setSlugState({ status: 'idle' }); return; }
    setSlugState({ status: 'checking' });
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/api/auth/slug-available?slug=${encodeURIComponent(s)}`);
        setSlugState({ status: r.available ? 'available' : 'taken' });
      } catch { setSlugState({ status: 'idle' }); }
    }, 400);
    return () => clearTimeout(t);
  }, [publicSlug, publicProfile, alias, username]);
  async function savePublicProfile() {
    // Sanitize the bio before saving — strips inline style/class junk that rides along
    // when text is pasted from another app, which otherwise bloats the HTML and gets it
    // truncated at the length cap (leaving the visible intro cut off mid-sentence).
    const cleanBio = htmlHasContent(bio) ? sanitizeHtml(bio).trim() : '';
    // Store the bare handle (strip any pasted URL/@) so display is consistent and links rebuild cleanly.
    const cleanSocials = {};
    for (const [k, v] of Object.entries(socials)) {
      const h = socialHandle(k, v);
      if (h) cleanSocials[k] = h;
    }
    // Only renamable keys with real text are sent; blanks mean "use the default".
    const cleanTitles = {};
    for (const k of Object.keys(SECTION_DEFAULT_TITLES)) {
      const v = sectionTitles[k];
      if (typeof v === 'string' && v.trim()) cleanTitles[k] = v.trim();
    }
    const updated = await onSave({ ...agent, publicProfile, publicSlug: publicSlug.trim(), bio: cleanBio, socials: cleanSocials, sectionOrder, hiddenSections: [...hiddenSections], sectionTitles: cleanTitles });
    // Reflect the server-confirmed slug (it may differ from what was typed — e.g. a unique
    // suffix was added) so the link shown is exactly the live one.
    const liveSlug = (updated && updated.publicSlug) || previewSlug;
    const on = updated ? !!updated.publicProfile : publicProfile;
    if (updated && updated.publicSlug) setPublicSlug(updated.publicSlug);
    if (updated) setPublicProfile(!!updated.publicProfile);
    setSocials(cleanSocials);   // reflect the normalized handles back into the inputs
    // Update the saved snapshot NOW so the live link/status refresh without a reload.
    setSavedPub({ on, slug: liveSlug });
    setPubMsg(on ? `Saved — live at /agent/${liveSlug}` : 'Saved (currently hidden).');
  }
  const liveUrl = (savedPub.on && savedPub.slug) ? `${window.location.origin}/agent/${savedPub.slug}` : '';
  function copyProfileLink() {
    const url = liveUrl || profileUrl;
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(() => setPubMsg('Link copied to clipboard.'), () => {});
  }
  function setSocial(key, val) { setSocials(s => ({ ...s, [key]: val })); setPubMsg(''); }
  // Rename a section's public heading (blank reverts to the default).
  function setSectionTitle(key, val) {
    setSectionTitles(prev => ({ ...prev, [key]: val }));
    setPubMsg('');
  }
  // Show/hide a section on the public profile (checkbox in the Section Order card).
  function toggleSectionShown(key) {
    setHiddenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setPubMsg('');
  }
  // Move a public-profile section up/down in the order list.
  function moveSection(key, delta) {
    setSectionOrder(prev => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setPubMsg('');
  }
  function insertProfileLinkIntoSignature() {
    if (!savedPub.on || !savedPub.slug) {
      setSigMsg('Publish your public profile first (in the section above), then add the link.');
      return;
    }
    // Absolute URL so it works in sent emails; resolves to ibetteragent.com in production.
    sigRef.current?.insertHTML(`<a href="${liveUrl}">View my profile &amp; listings</a>`);
    setSigMsg('Profile link inserted — review, then Save Signature.');
  }

  function pickPhoto(e) {
    const f = e.target.files[0];
    if (!f) return;
    e.target.value = '';
    resizeImage(f, 256, dataUrl => { setPhoto(dataUrl); setMsg(''); });
  }
  function saveProfile() {
    onSave({ ...agent, photo, alias: alias.trim(), email: email.trim(), phone: phone.trim(), website: website.trim(), mls, licenseId: licenseId.trim(), mlsBrokerId: mlsBrokerId.trim(), languages });
    // Stay on the page and show the confirmation — same behavior as every other
    // section's Save (this used to auto-navigate back, which read as a bug).
    setMsg('Profile saved.');
  }
  function saveSignature() {
    onSave({ ...agent, signature });   // appended to the end of every reminder/message you send
    setSigMsg('Signature saved.');
  }
  function saveCreds() {
    // Regular accounts log in with their email — the username follows it and isn't
    // editable here (change the email in the profile section above). Only the system
    // operator has an editable word-based username.
    if (agent.system && !username.trim()) { setCredMsg('Username cannot be empty.'); return; }
    if (password && password !== confirm) { setCredMsg('Passwords do not match.'); return; }
    if (password) { const pe = passwordError(password); if (pe) { setCredMsg(pe); return; } }
    onSave({ ...agent, ...(agent.system ? { username: username.trim() } : {}), ...(password ? { password } : {}) });
    setPassword(''); setConfirm('');
    setCredMsg('Credentials updated.');
  }

  const initial = (alias || username || '?').trim().charAt(0).toUpperCase();
  const accessLog = [...(agent.impersonations || [])].reverse();   // newest first

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div>
          <h2>Profile</h2>
          <p>Manage your display name, photo, and login.</p>
        </div>
        <div className="profile-header-actions">
          {onGettingStarted && (
            <button className="profile-getstarted" onClick={onGettingStarted}>
              <Icon name="check-circle" size={16} /> Getting Started
            </button>
          )}
          <a className="profile-guide-link" href={`${process.env.PUBLIC_URL || ''}/agent-quick-start.html`} target="_blank" rel="noreferrer">
            <Icon name="file" size={16} /> Agent Quick Start Guide
          </a>
          <button className="profile-logout" onClick={onLogout}><Icon name="logout" size={16} /> Log Out</button>
        </div>
      </div>

      <div className="profile-card">
        <div className="profile-section-title">Login Credentials</div>
        <label className="profile-label">{agent.system ? 'Username' : 'Login (email)'}</label>
        {agent.system ? (
          <input className="profile-input" value={username} onChange={e => { setUsername(e.target.value); setCredMsg(''); }} />
        ) : (
          <>
            <input className="profile-input" value={agent.username || ''} readOnly disabled />
            <p className="profile-access-hint">You log in with your email. To change it, update your <strong>Email</strong> in the profile section above.</p>
          </>
        )}
        <label className="profile-label">New Password</label>
        <PasswordInput className="profile-input" placeholder="Leave blank to keep current"
          value={password} onChange={e => { setPassword(e.target.value); setCredMsg(''); }} />
        <label className="profile-label">Confirm Password</label>
        <PasswordInput className="profile-input"
          value={confirm} onChange={e => { setConfirm(e.target.value); setCredMsg(''); }} />
        <div className="profile-actions">
          {credMsg && <span className={credMsg === 'Credentials updated.' ? 'profile-saved' : 'profile-err'}>{credMsg}</span>}
          <button className="rt-save-btn" onClick={saveCreds}>Update Credentials</button>
        </div>
      </div>

      <div className="profile-card">
        <div className="profile-section-title">Display</div>
        <div className="profile-photo-row">
          {photo
            ? <img className="profile-photo" src={photo} alt={alias || username} />
            : <span className="profile-photo profile-photo-initial">{initial}</span>}
          <div className="profile-photo-actions">
            <button className="profile-photo-btn" onClick={() => fileRef.current.click()}>Upload Photo</button>
            {photo && <button className="profile-photo-remove" onClick={() => { setPhoto(''); setMsg(''); }}>Remove</button>}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickPhoto} />
          </div>
        </div>
        <label className="profile-label">Full name <span className="profile-optional">(shown on your public profile, in messages, and to your contacts)</span></label>
        <input className="profile-input" value={alias} placeholder="Your full name"
          onChange={e => { setAlias(e.target.value); setMsg(''); }} />
        <label className="profile-label">Email <span className="profile-optional">(optional — add it to enable password reset)</span></label>
        <input type="email" className="profile-input" value={email} onChange={e => { setEmail(e.target.value); setMsg(''); }} />
        <label className="profile-label">Phone Number <span className="profile-optional">(optional)</span></label>
        <input type="tel" className="profile-input" placeholder="XXX-XXX-XXXX" maxLength={12} inputMode="numeric"
          value={phone} onChange={e => { setPhone(formatPhone(e.target.value)); setMsg(''); }} />
        <label className="profile-label">Website <span className="profile-optional">(optional — added as a link to your built-in reminders so clients can click through)</span></label>
        <input type="url" className="profile-input" placeholder="https://your-site.com" value={website} onChange={e => { setWebsite(e.target.value); setMsg(''); }} />
        {mlsEditable ? (
          <>
            <label className="profile-label">Supported MLS <span className="profile-optional">(optional — required to import contacts &amp; properties)</span></label>
            <select className="profile-input" value={mlsOptions.some(m => m.id === mls) ? mls : ''}
              onChange={e => { setMls(e.target.value); setMsg(''); }}>
              <option value="">— None —</option>
              {mlsOptions.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </>
        ) : (
          <>
            <label className="profile-label">Supported MLS</label>
            <input className="profile-input" value={orgMlsName || 'Set by your organization'} readOnly />
          </>
        )}
        <label className="profile-label">License ID <span className="profile-optional">(optional)</span></label>
        <input className="profile-input" value={licenseId} onChange={e => { setLicenseId(e.target.value); setMsg(''); }} />
        <label className="profile-label">MLS Broker ID <span className="profile-optional">(optional)</span></label>
        <input className="profile-input" value={mlsBrokerId} onChange={e => { setMlsBrokerId(e.target.value); setMsg(''); }} />
        <label className="profile-label">Languages <span className="profile-optional">(optional)</span></label>
        <div className="profile-lang-opts">
          {LANG_OPTIONS.map(([code, label]) => (
            <label key={code} className="profile-lang-chip">
              <input type="checkbox" checked={languages.includes(code)} onChange={() => toggleLang(code)} /> {label}
            </label>
          ))}
        </div>
        <div className="profile-actions">
          {msg && <span className="profile-saved">✓ {msg}</span>}
          <button className="rt-save-btn" onClick={saveProfile}>Save Profile</button>
        </div>
      </div>

      {canPublicProfile && (
      <div className="profile-card">
        <div className="profile-section-title">Public Profile Page</div>
        <p className="profile-access-hint">A public web page clients can view without logging in — your photo, bio, contact info, social links, and your sold/active listings. Add the link to your email signature so clients click through (every click is tracked and boosts that contact's engagement score).</p>
        <label className="profile-toggle-row">
          <input type="checkbox" checked={publicProfile} onChange={e => { setPublicProfile(e.target.checked); setPubMsg(''); }} />
          <span>Publish my public profile page</span>
        </label>

        <label className="profile-label">Profile URL</label>
        <div className="profile-slug-row">
          <span className="profile-slug-prefix">{window.location.host}/agent/</span>
          <input className="profile-input profile-slug-input" placeholder={clientSlugify(alias || username) || 'your-name'}
            value={publicSlug} onChange={e => { setPublicSlug(e.target.value); setPubMsg(''); }} />
        </div>
        <div className="profile-slug-preview">
          {liveUrl ? (
            <>
              <a href={liveUrl} target="_blank" rel="noreferrer">{`${window.location.host}/agent/${savedPub.slug}`}</a>
              <span className="profile-slug-live">● Live</span>
              <button type="button" className="profile-copy-btn" onClick={copyProfileLink}>Copy link</button>
            </>
          ) : (
            <span className="profile-slug-pending">{window.location.host}/agent/{previewSlug} — not live yet; turn on the toggle above and click “Save Public Profile”.</span>
          )}
        </div>
        {publicProfile && slugState.status !== 'idle' && (
          <div className={`profile-slug-status profile-slug-${slugState.status}`}>
            {slugState.status === 'checking' && 'Checking availability…'}
            {slugState.status === 'available' && '✓ This link is available'}
            {slugState.status === 'taken' && '⚠ Taken — pick another, or we’ll add a short unique suffix when you save'}
          </div>
        )}

        <label className="profile-label">Short Introduction</label>
        <RichEditor docKey={`bio-${agent.id}`} value={bio}
          onChange={html => { setBio(html); setPubMsg(''); }}
          placeholder="A sentence or two about you and how you help clients — bold, lists, and links are supported." />

        <label className="profile-label">Social Handles <span className="profile-optional">(optional)</span></label>
        <p className="profile-access-hint" style={{ marginTop: -4, marginBottom: 8 }}>Just your username or ID — we build the full link for you. You can also paste a full profile URL.</p>
        {SOCIAL_KEYS.map((key) => {
          const cfg = SOCIALS[key];
          const resolved = socialLink(key, socials[key]);
          return (
            <div key={key} className="profile-social-row">
              <input className="profile-input profile-social-input" type="text"
                placeholder={`${cfg.label} — ${cfg.hint}`}
                value={socials[key] || ''} onChange={e => setSocial(key, e.target.value)} />
              {resolved && (
                <span className="profile-social-preview">
                  {cfg.icon} {resolved.isLink
                    ? <a href={resolved.url} target="_blank" rel="noreferrer">{resolved.url}</a>
                    : <>WeChat ID: {resolved.display} <em>(shown as copyable text — WeChat has no web link)</em></>}
                </span>
              )}
            </div>
          );
        })}

        <div className="profile-actions">
          {pubMsg && <span className="profile-saved"><Icon name="check" size={15} /> {pubMsg}</span>}
          <button className="rt-save-btn" onClick={savePublicProfile}>Save Public Profile</button>
        </div>
        <p className="profile-access-hint" style={{ marginTop: 10 }}>Name, photo, email and phone come from the <strong>Display</strong> section above. Your listings are pulled from your Properties automatically — client names are never shown.</p>
      </div>
      )}

      <div className="profile-card">
        <div className="profile-section-title">Email Signature</div>
        <p className="profile-access-hint">Automatically added to the end of every reminder and message you send. Include your name, title, brokerage, phone, or anything else you'd like clients to see.</p>
        <RichEditor ref={sigRef} docKey={`signature-${agent.id}`} value={signature}
          onChange={html => { setSignature(html); setSigMsg(''); }}
          placeholder="e.g. Jane Doe · Realtor® · Acme Realty · (555) 123-4567" />
        {canPublicProfile && (
          <p className="profile-access-hint" style={{ marginTop: 8 }}>
            Tip: add your public profile link here so it goes out with every reminder and message — clients who click it land on your profile, and the click is tracked (boosting that contact's engagement score).
          </p>
        )}
        <div className="profile-actions">
          {sigMsg && <span className="profile-saved"><Icon name="check" size={15} /> {sigMsg}</span>}
          {canPublicProfile && <button type="button" className="profile-copy-btn" onClick={insertProfileLinkIntoSignature}>Insert my profile link</button>}
          <button className="rt-save-btn" onClick={saveSignature}>Save Signature</button>
        </div>
      </div>

      {accessLog.length > 0 && (
        <div className="profile-card">
          <div className="profile-section-title">Support Access History</div>
          <p className="profile-access-hint">Times an administrator signed in to your account for support. If anything looks wrong, contact your administrator.</p>
          <ul className="profile-access-list">
            {accessLog.map((e, i) => (
              <li key={i} className="profile-access-item">
                <span className="profile-access-when">{fmtTime(e.at)}</span>
                <span className="profile-access-by">by {e.by}</span>
                <span className="profile-access-note">“{e.note}”</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canPublicProfile && (
        <div className="profile-card">
          <div className="profile-section-title">Public Profile Section Order</div>
          <p className="profile-access-hint">
            Choose which sections appear on your public profile (uncheck to hide one), arrange their order,
            and optionally rename a section's heading — e.g. give it a Chinese title. Leave a name blank to
            use the default (shown in gray). Sections with nothing to show are hidden automatically either way.
          </p>
          <ul className="profile-sections">
            {sectionOrder.map((key, i) => {
              const shown = !hiddenSections.has(key);
              const renamable = key in SECTION_DEFAULT_TITLES;
              return (
                <li key={key} className={`profile-section-row ${shown ? '' : 'profile-section-hidden'}`}>
                  <input type="checkbox" className="profile-section-check" checked={shown}
                    title={shown ? 'Shown on your public profile — uncheck to hide' : 'Hidden from your public profile — check to show'}
                    aria-label={`Show ${SECTION_LABELS[key]} on my public profile`}
                    onChange={() => toggleSectionShown(key)} />
                  <span className="profile-section-num">{i + 1}</span>
                  {renamable ? (
                    <span className="profile-section-namecell">
                      <input className="profile-section-name-input" type="text" maxLength={60}
                        placeholder={SECTION_DEFAULT_TITLES[key]}
                        value={sectionTitles[key] || ''}
                        onChange={e => setSectionTitle(key, e.target.value)}
                        title={`Public heading for this section — blank uses "${SECTION_DEFAULT_TITLES[key]}"`}
                        aria-label={`Custom heading for ${SECTION_LABELS[key]}`} />
                      <span className="profile-section-sublabel">{SECTION_LABELS[key]}</span>
                    </span>
                  ) : (
                    <span className="profile-section-label">{SECTION_LABELS[key] || key}</span>
                  )}
                  {!shown && <span className="profile-section-hidden-tag">hidden</span>}
                  <button type="button" className="profile-section-btn" disabled={i === 0}
                    title="Move up" aria-label={`Move ${SECTION_LABELS[key]} up`}
                    onClick={() => moveSection(key, -1)}>▲</button>
                  <button type="button" className="profile-section-btn" disabled={i === sectionOrder.length - 1}
                    title="Move down" aria-label={`Move ${SECTION_LABELS[key]} down`}
                    onClick={() => moveSection(key, 1)}>▼</button>
                </li>
              );
            })}
          </ul>
          <button type="button" className="profile-section-reset"
            onClick={() => { setSectionOrder([...PROFILE_SECTION_KEYS]); setHiddenSections(new Set()); setSectionTitles({}); setPubMsg(''); }}>
            Reset to default (all shown, original order &amp; names)
          </button>
          <div className="profile-actions">
            {pubMsg && <span className="profile-saved"><Icon name="check" size={15} /> {pubMsg}</span>}
            <button className="rt-save-btn" onClick={savePublicProfile}>Save Section Order</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
